import type { HotelChainRep } from "@/lib/types/hotel";

/** The default Nowadays organization that holds globally-shared chain reps. Exported so the other
 *  chain-rep readers key off one value rather than each redeclaring it. Lives here, not in
 *  `hotel-chain-reps`, so this pure module has no database import. */
export const GLOBAL_ORG_ID = 999;

/** Chain reps after {@link applyEnterpriseRepPrecedence}, plus who stands in for the dropped ones. */
export interface EnterpriseRepPrecedence<T> {
  /** The chain reps to use: global-org reps removed when the enterprise has its own. */
  chainReps: T[];
  /** The enterprise's rep emails when a global rep was actually dropped for them, else `[]`. A
   *  venue with no email of its own is addressed to these in place of the dropped global rep. */
  replacementRepEmails: string[];
}

/**
 * Drops the global Nowadays chain reps (`org_id === 999`) once the event's enterprise has named
 * its own rep for this venue or chain. The global rep is a stand-in for "nobody better known";
 * an enterprise that set up its own contact (e.g. a GSO who handles that account) wants only
 * that contact copied, not the generic one alongside. Org-specific chain reps are kept.
 *
 * @param chainReps - The chain reps resolved by `getPreferredChainRep`.
 * @param enterpriseRepEmails - The enterprise's preferred rep emails for this venue/chain.
 * @returns The chain reps to use, and the enterprise reps that replaced any dropped global rep.
 */
export function applyEnterpriseRepPrecedence<T extends Pick<HotelChainRep, "org_id">>(
  chainReps: T[],
  enterpriseRepEmails: string[]
): EnterpriseRepPrecedence<T> {
  if (enterpriseRepEmails.length === 0) return { chainReps, replacementRepEmails: [] };
  const kept = chainReps.filter((rep) => rep.org_id !== GLOBAL_ORG_ID);
  return { chainReps: kept, replacementRepEmails: kept.length < chainReps.length ? enterpriseRepEmails : [] };
}
